import React from "react";
import { useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { useTranslation } from "next-i18next";
import { serialize } from "next-mdx-remote/serialize";

import getTranslationByPath from "../services/getTranslationByPath.mjs";
import Hero from "../components/Common/Hero";
import TextLeft from "../components/Common/TextLeft";
import TextRight from "../components/Common/TextRight";
import Container from "../components/Common/Container";
import Link from "../components/Common/Link";
import RequestADemo from "../components/Common/RequestADemo";
import Clients from "../components/Common/Clients";
import Testimonial from "../components/Targets/Testimonial";

import heroImage from "../public/static/images/publics/hero.png";
import whatsTodayImage from "../public/static/images/publics/reconcilier-les-citoyens-avec-la-vie-politique.png";
import ourMissionImage from "../public/static/images/publics/mettre-en-oeuvre-des-dispositifs-de-participation-efficaces.png";
import MetaTags from "../components/Common/MetaTags";
import testimonialImage from "../public/static/images/publics/Axel-Comhaire.jpg";
import caseStudy1Image from "../public/static/images/publics/Comment-mobiliser-10-pourcents-des-citoyens-pour-un-premier-Budget-Participatif.jpg";
import caseStudy2Image from "../public/static/images/publics/Comment-choisir-un-projet-qui-repond-vraiment-aux-attentes-des-citoyens.jpg";
import CloudinaryImage from "../components/Common/CloudinaryImage/CloudinaryImage.js";

export default function Publics({
  heroSubtitle,
  whatsTodayContent,
  ourMissionContent,
  caseStudy1Content,
  caseStudy2Content,
}) {
  const theme = useTheme();
  const { t } = useTranslation(["publics", "common"]);

  return (
    <>
      <MetaTags scope="publics" />
      <Box bgcolor={theme.palette.alternate.main} position={"relative"}>
        <Container position="relative" zIndex={2}>
          <Hero
            pageTitle={t("navbar.publics", { ns: "common" })}
            title1={t("hero.title_1")}
            title2={t("hero.title_2")}
            title3={t("hero.title_3")}
            subtitle={heroSubtitle}
            image={
              <CloudinaryImage
                src={heroImage}
                alt={`${t("page_title")} hero`}
                width={464}
                height={367}
                priority
              />
            }
          />
        </Container>
        <Box
          component={"svg"}
          preserveAspectRatio="none"
          xmlns="http://www.w3.org/2000/svg"
          x="0px"
          y="0px"
          viewBox="0 0 1921 273"
          sx={{
            position: "absolute",
            width: "100%",
            left: 0,
            bottom: 0,
            right: 0,
            zIndex: 1,
            height: "35%",
          }}
        >
          <polygon
            fill={theme.palette.background.paper}
            points="0,273 1921,273 1921,0 "
          />
        </Box>
      </Box>

      <Container>
        <TextLeft
          title1={t("whats_today.title_1")}
          title2={t("whats_today.title_2")}
          title3={t("whats_today.title_3")}
          content={whatsTodayContent}
          image={
            <CloudinaryImage
              src={whatsTodayImage}
              alt={`${t("page_title")} illustration`}
              width={598}
              height={557}
              placeholder="blur"
            />
          }
        />
      </Container>

      <Container>
        <TextRight
          title1={t("our_mission.title_1")}
          title2={t("our_mission.title_2")}
          title3={t("our_mission.title_3")}
          content={ourMissionContent}
          image={
            <CloudinaryImage
              src={ourMissionImage}
              alt={`${t("page_title")} illustration`}
              width={693}
              height={798}
              placeholder="blur"
            />
          }
          cta={
            <Button
              href="/plateforme-consultation-citoyenne"
              variant="outlined"
              color="primary"
              component={Link}
              size="large"
            >
              {t("our_mission.cta")}
            </Button>
          }
        />
      </Container>

      <Box mt={25} mb={-5}>
        <Clients title={t("clients.title")} />
      </Box>

      <Box bgcolor={theme.palette.alternate.dark} marginTop={20}>
        <Container position="relative" zIndex={2}>
          <Testimonial
            header={t("testimonial.header")}
            content={t("testimonial.content")}
            fullName={t("testimonial.full_name")}
            jobTitle={t("testimonial.job_title")}
            image={
              <CloudinaryImage
                src={testimonialImage}
                alt={`${t("testimonial.full_name")} ${t(
                  "testimonial.job_title"
                )}`}
                width={50}
                height={50}
                placeholder="blur"
                layout="fixed"
              />
            }
          />
        </Container>
      </Box>

      <Container>
        <TextRight
          header={t("case_study_1.header")}
          title1={t("case_study_1.title_1")}
          content={caseStudy1Content}
          image={
            <CloudinaryImage
              src={caseStudy1Image}
              alt={`${t("page_title")} illustration`}
              width={378}
              height={354}
              placeholder="blur"
            />
          }
          cta={
            <Button
              href="/static/resources/cas_d-usage_bertrix.pdf"
              variant="contained"
              color="primary"
              component={Link}
              target="_blank"
              size="large"
            >
              {t("case_study_1.cta")}
            </Button>
          }
        />
      </Container>

      <Container marginBottom={20}>
        <TextLeft
          header={t("case_study_2.header")}
          title1={t("case_study_2.title_1")}
          content={caseStudy2Content}
          image={
            <CloudinaryImage
              src={caseStudy2Image}
              alt={`${t("page_title")} illustration`}
              width={379}
              height={321}
              placeholder="blur"
            />
          }
          cta={
            <Button
              href="/static/resources/cas-d-usage-thionville.pdf"
              variant="contained"
              color="primary"
              component={Link}
              size="large"
              target="_blank"
            >
              {t("case_study_2.cta")}
            </Button>
          }
        />
      </Container>

      <RequestADemo title={t("request_a_demo.title")} />
    </>
  );
}

export async function getStaticProps({ locale }) {
  const { content } = getTranslationByPath("publics", locale);

  return {
    props: {
      ...(await serverSideTranslations(locale, ["common", "publics"])),
      heroSubtitle: await serialize(content.hero.subtitle),
      whatsTodayContent: await serialize(content.whats_today.content),
      ourMissionContent: await serialize(content.our_mission.content),
      caseStudy1Content: await serialize(content.case_study_1.content),
      caseStudy2Content: await serialize(content.case_study_2.content),
    },
  };
}
